import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function Register() {
    const { register } = useAuth();
    const nav = useNavigate();
    const [form, setForm] = useState({ full_name: "", email: "", password: "", exam_target: "NEET" });
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await register(form);
            toast.success("Welcome to Prepcheck");
            nav("/dashboard", { replace: true });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Registration failed");
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-[calc(100vh-8rem)] flex">
            <div className="hidden lg:block lg:w-1/2 relative border-r border-border bg-muted/30">
                <div className="absolute inset-0 grid-lines opacity-40" />
                <div className="relative h-full flex flex-col justify-between p-12">
                    <div>
                        <span className="overline text-primary">▮ Register</span>
                        <h2 className="mt-6 font-heading font-black text-5xl leading-tight tracking-tighter">
                            Start your <br />rank-hunt.
                        </h2>
                    </div>
                    <ul className="text-sm text-muted-foreground space-y-2 font-mono">
                        <li>▮ 15 free generations + 15 AI analytics reports</li>
                        <li>▮ Unlimited practice & scoring, forever</li>
                        <li>▮ NTA-style exam interface</li>
                    </ul>
                </div>
            </div>

            <div className="flex-1 flex items-center justify-center p-6">
                <form onSubmit={submit} data-testid="register-form" className="w-full max-w-md">
                    <h1 className="font-heading font-black text-3xl tracking-tighter">Create account</h1>
                    <p className="mt-2 text-sm text-muted-foreground">
                        Have one? <Link to="/login" className="text-primary underline underline-offset-4">Sign in</Link>
                    </p>

                    <div className="mt-8 space-y-4">
                        <div>
                            <Label className="overline">Full name</Label>
                            <Input data-testid="register-fullname-input" required value={form.full_name}
                                onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="rounded-sm mt-2" />
                        </div>
                        <div>
                            <Label className="overline">Email</Label>
                            <Input data-testid="register-email-input" type="email" required value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm mt-2" />
                        </div>
                        <div>
                            <Label className="overline">Password</Label>
                            <Input data-testid="register-password-input" type="password" required minLength={6} value={form.password}
                                onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-sm mt-2" />
                        </div>
                        <div>
                            <Label className="overline">Exam target</Label>
                            <Select value={form.exam_target} onValueChange={(v) => setForm({ ...form, exam_target: v })}>
                                <SelectTrigger data-testid="register-exam-target-select" className="rounded-sm mt-2"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="NEET">NEET</SelectItem>
                                    <SelectItem value="JEE">JEE</SelectItem>
                                    <SelectItem value="BOTH">Both</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <Button data-testid="register-submit-btn" type="submit" disabled={busy} className="w-full rounded-sm bg-primary hover:bg-primary/90 btn-press">
                            {busy ? "Creating…" : "Create account"}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}
